import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignStepsAPI, campaignsAPI } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import { TableSkeleton } from '../components/LoadingSpinner';
import { useToast } from '../hooks/useToast';
import { LogsIcon, WarningIcon } from '../components/Icons';
import './CampaignSteps.css';

const CHANNELS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'INSTAGRAM', label: 'Instagram' },
];

const CONDITIONS = [
  { value: 'ANY', label: 'Any - Always execute' },
  { value: 'NO_REPLY', label: 'No Reply - Only if no reply received' },
  { value: 'REPLIED', label: 'Replied - Only if reply received' },
];

export default function CampaignSteps() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [steps, setSteps] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [formData, setFormData] = useState({
    step_order: 1,
    channels: ['EMAIL'], // Multiple channels support
    delay_days: 0,
    condition: 'ANY',
    send_time: '09:00'
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (campaignId) {
      loadCampaign();
      loadSteps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const loadCampaign = async () => {
    try {
      const response = await campaignsAPI.get(campaignId);
      setCampaign(response.data);
    } catch (error) {
      console.error('Failed to load campaign:', error);
      toast.error('Failed to load campaign details');
    }
  };

  const loadSteps = async () => {
    try {
      setLoading(true);
      const response = await campaignStepsAPI.getAll(campaignId);
      // Handle paginated response
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setSteps(data);
    } catch (error) {
      console.error('Failed to load steps:', error);
      toast.error('Failed to load campaign steps');
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.channels || formData.channels.length === 0) {
      errors.channels = 'At least one channel is required';
    }
    if (!formData.condition) errors.condition = 'Condition is required';
    if (formData.step_order < 1) errors.step_order = 'Step order must be at least 1';
    if (formData.delay_days < 0) errors.delay_days = 'Delay cannot be negative';
    if (!formData.send_time) errors.send_time = 'Send time is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix form errors');
      return;
    }

    try {
      setActionLoading(true);
      const data = {
        ...formData,
        campaign: campaignId,
        step_order: parseInt(formData.step_order),
        delay_days: parseInt(formData.delay_days),
        channels: formData.channels // Send channels array
      };

      if (editingStep) {
        await campaignStepsAPI.update(editingStep.id, data);
        toast.success('Step updated successfully');
      } else {
        await campaignStepsAPI.create(data);
        toast.success('Step created successfully');
      }
      
      setShowModal(false);
      setEditingStep(null);
      setFormData({
        step_order: steps.length + 1,
        channels: ['EMAIL'],
        delay_days: 0,
        condition: 'ANY',
        send_time: '09:00'
      });
      setFormErrors({});
      loadSteps();
    } catch (error) {
      console.error('Failed to save step:', error);
      toast.error('Failed to save step. Check console for details.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (step) => {
    setEditingStep(step);
    // Handle both new channels array and legacy channel field
    const channels = step.channels && step.channels.length > 0 
      ? step.channels 
      : (step.channel ? [step.channel] : ['EMAIL']);
    
    setFormData({
      step_order: step.step_order,
      channels: channels,
      delay_days: step.delay_days,
      condition: step.condition,
      send_time: step.send_time || '09:00'
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this step? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(id);
      await campaignStepsAPI.delete(id);
      toast.success('Step deleted successfully');
      loadSteps();
    } catch (error) {
      console.error('Failed to delete step:', error);
      toast.error('Failed to delete step. Check console for details.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNew = () => {
    setEditingStep(null);
    setFormData({
      step_order: steps.length + 1,
      channels: ['EMAIL'],
      delay_days: 0,
      condition: 'ANY',
      send_time: '09:00'
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingStep(null);
    setFormData({
      step_order: steps.length + 1,
      channels: ['EMAIL'],
      delay_days: 0,
      condition: 'ANY',
      send_time: '09:00'
    });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Campaign Steps</h1>
        </div>
        <Card>
          <TableSkeleton rows={5} cols={6} />
        </Card>
      </div>
    );
  }

  if (!campaignId) {
    return (
      <div className="content-wrapper">
        <Card>
          <div className="empty-state">
            <div className="empty-state-icon">
              <WarningIcon size={48} style={{ opacity: 0.5 }} />
            </div>
            <div className="empty-state-title">No campaign selected</div>
            <div className="empty-state-description">
              Please select a campaign first
            </div>
            <Button variant="primary" onClick={() => navigate('/campaigns')} style={{ marginTop: 'var(--spacing-lg)' }}>
              Go to Campaigns
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Campaign Steps</h1>
          <p className="page-subtitle">
            {campaign ? `Campaign: ${campaign.name}` : 'Manage campaign steps'}
          </p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => navigate('/campaigns')}>
            ← Back to Campaigns
          </Button>
          <Button variant="primary" onClick={handleNew}>
            + Add Step
          </Button>
        </div>
      </div>

      {steps.length === 0 ? (
        <Card>
          <div className="empty-state">
            <div className="empty-state-icon">
              <LogsIcon size={48} style={{ opacity: 0.5 }} />
            </div>
            <div className="empty-state-title">No steps yet</div>
            <div className="empty-state-description">
              Add your first step to get started with this campaign
            </div>
            <Button variant="primary" onClick={handleNew} style={{ marginTop: 'var(--spacing-lg)' }}>
              Add First Step
            </Button>
          </div>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Channel</th>
                  <th>Condition</th>
                  <th>Delay (days)</th>
                  <th>Send Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {steps.map(step => (
                  <tr key={step.id}>
                    <td>
                      <span className="badge">{step.step_order}</span>
                    </td>
                    <td>
                      {(() => {
                        // Display channels - support both new and legacy format
                        const channels = step.channels && step.channels.length > 0 
                          ? step.channels 
                          : (step.channel ? [step.channel] : []);
                        return channels.map((ch, idx) => (
                          <span key={`${step.id}-${ch}-${idx}`} className="badge badge-channel" style={{ marginRight: '4px' }}>
                            {ch}
                          </span>
                        ));
                      })()}
                    </td>
                    <td>
                      <span className="text-secondary">{step.condition}</span>
                    </td>
                    <td>
                      <span className="text-secondary">{step.delay_days}</span>
                    </td>
                    <td>
                      <span className="text-secondary">{step.send_time || 'N/A'}</span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEdit(step)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(step.id)}
                          loading={actionLoading === step.id}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal show={showModal} onClose={handleClose} title={editingStep ? 'Edit Step' : 'New Step'} width="600px">
        <form onSubmit={handleSubmit}>
          <Input
            label="Step Order"
            type="number"
            value={formData.step_order}
            onChange={(e) => setFormData({ ...formData, step_order: e.target.value })}
            required
            min="1"
            error={formErrors.step_order}
            helperText="Order in which this step executes"
          />

          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <label style={{ display: 'block', marginBottom: 'var(--spacing-sm)', fontWeight: 500 }}>
              Channels <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
              {CHANNELS.map(channel => (
                <label
                  key={channel.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: formData.channels.includes(channel.value)
                      ? 'var(--color-primary-light)'
                      : 'transparent',
                    borderColor: formData.channels.includes(channel.value)
                      ? 'var(--color-primary)'
                      : 'var(--color-border)',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.channels.includes(channel.value)}
                    onChange={(e) => {
                      const newChannels = e.target.checked
                        ? [...formData.channels, channel.value]
                        : formData.channels.filter(ch => ch !== channel.value);
                      setFormData({ ...formData, channels: newChannels });
                    }}
                    style={{ marginRight: 'var(--spacing-sm)' }}
                  />
                  <span>{channel.label}</span>
                </label>
              ))}
            </div>
            {formErrors.channels && (
              <div style={{ color: 'var(--color-danger)', fontSize: '0.875rem', marginTop: 'var(--spacing-xs)' }}>
                {formErrors.channels}
              </div>
            )}
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
              Select one or more channels for this step
            </div>
          </div>

          <Input
            label="Condition"
            type="select"
            value={formData.condition}
            onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
            required
            options={CONDITIONS}
            error={formErrors.condition}
          />

          <Input
            label="Delay (days)"
            type="number"
            value={formData.delay_days}
            onChange={(e) => setFormData({ ...formData, delay_days: e.target.value })}
            required
            min="0"
            error={formErrors.delay_days}
            helperText="Days to wait before sending this step"
          />

          <Input
            label="Send Time (HH:MM)"
            type="time"
            value={formData.send_time}
            onChange={(e) => setFormData({ ...formData, send_time: e.target.value })}
            required
            error={formErrors.send_time}
            helperText="Time of day to send (lead's timezone)"
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-xl)' }}>
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={actionLoading}>
              {editingStep ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
